<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">Procedury</span>
        <h3 class="page-title">
          <template v-if="isEdit">Edycja procedury</template>
          <template v-else>Dodawanie procedury</template>
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/procedury')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          Wróć do listy
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          Zapisz zmiany
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-card class="mb-5">
        <d-tabs card>
          <!-- Dane ogólne -->
          <d-tab title="Dane ogólne" active>
            <h4 class="mb-3 pb-3 border-bottom">
              Dane ogólne
            </h4>

            <validation-observer
              ref="procedure-form"
              tag="form">
              <div class="form-group">
                <label for="name">
                  Nazwa procedury:
                </label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    v-model="data.name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <h5 class="mb-3 mt-5 pb-3 border-bottom d-flex">
                Procesy procedury

                <d-button
                  @click.prevent="addProcess()"
                  theme="success"
                  outline
                  class="ml-auto">
                  Dodaj process
                </d-button>
              </h5>

              <d-card v-if="loaded">
                <table class="table mt-0 mb-0 full-width">
                  <thead class="bg-light">
                    <tr>
                      <th scope="col" class="border-0">Nazwa procesu</th>
                      <th scope="col" class="border-0" style="width: 200px;">Rodzaj procesu</th>
                      <th scope="col" class="border-0" style="width: 200px;">Koszt</th>
                      <th scope="col" class="border-0" style="width: 240px;">Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(process, index) of data.processes"
                      :key="'process-' + index">
                      <td class="align-top">
                        <validation-provider
                          rules="required"
                          :name="'process-name-' + index"
                          v-slot="{ errors }">
                          <d-input
                            :state="errors.length ? 'invalid' : null"
                            v-model="process.name"
                            placeholder="Podaj nazwę procesu" />
                          <div class="invalid-feedback">
                            {{ errors[0] }}
                          </div>
                        </validation-provider>
                      </td>
                      <td class="align-top">
                        <validation-provider
                          rules="required"
                          :name="'process-type-' + index"
                          v-slot="{ errors }">
                          <d-form-select
                            v-model="process.type"
                            :state="errors.length ? 'invalid' : null">
                            <option value="">Wybierz rodzaj procesu</option>
                            <option value="PATTERN">Wykrajanie</option>
                            <option value="SEWING">Szycie</option>
                            <option value="VERIFICATION">Weryfikacja</option>
                          </d-form-select>
                          <div class="invalid-feedback">
                            {{ errors[0] }}
                          </div>
                        </validation-provider>
                      </td>
                      <td class="align-top">
                        <validation-provider
                          v-if="process.type !== 'VERIFICATION'"
                          rules="required|min_value:0|only-numbers"
                          :name="'process-cost-' + index"
                          v-slot="{ errors }">
                          <d-input-group append="zł">
                            <d-input
                              v-model="process.cost"
                              class="text-right"
                              :state="errors.length ? 'invalid' : null"
                              />
                          </d-input-group>
                        </validation-provider>
                        <small
                          v-else
                          class="d-block text-center pt-2">
                          nie dotyczy
                        </small>
                      </td>
                      <td
                        class="align-top">
                        <d-button
                          theme="primary"
                          outline
                          @click.prevent="moveProcess(index, 'down')"
                          :disabled="index === data.processes.length - 1 || data.processes.length === 1"
                          title="Przesuń w dół">
                          <i class="material-icons">south</i>
                        </d-button>

                        <d-button
                          theme="primary"
                          outline
                          @click.prevent="moveProcess(index, 'up')"
                          title="Przesuń w górę"
                          :disabled="index === 0 || data.processes.length === 1"
                          class="ml-2">
                          <i class="material-icons">north</i>
                        </d-button>

                        <d-button
                          theme="danger"
                          @click.prevent="removeProcess(index)"
                          title="Usuń"
                          class="ml-2">
                          <i class="material-icons">delete</i> Usuń
                        </d-button>
                      </td>
                    </tr>
                    <tr v-if="data.processes.length === 0">
                      <td
                        colspan="5"
                        class="text-center">
                        Brak procesów dla tej procedury&hellip;
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div v-if="!loaded" class="pb-3 pt-3 text-center">
                  Trwa ładowanie danych&hellip;
                </div>

                <d-alert
                  v-if="loadError"
                  show
                  theme="warning">
                  Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
                </d-alert>
              </d-card>
            </validation-observer>

            <div
              v-if="!allDataLoaded"
              class="pb-3 pt-3 text-center">
              Trwa ładowanie danych&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
            </d-alert>
          </d-tab>

          <d-tab title="Wydajność pracowników">
            <h4 class="mb-3 pb-3 border-bottom">
              Wydajność pracowników
            </h4>

            <d-alert
              v-if="allDataLoaded && !workers.length"
              show
              theme="warning">
              Brak pracowników
            </d-alert>

            <table
              v-if="workers.length"
              class="table mt-0 mb-0 full-width">
              <thead class="bg-light">
                <tr>
                  <th scope="col" class="border-0 w-75">Pracownik</th>
                  <th scope="col" class="border-0 w-25">Może wykonać w ciągu dnia</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(worker, index) of data.users"
                  :key="'worker-' + index">
                  <td class="align-middle">
                    {{ getWorkerNameById(worker.id) }}
                  </td>
                  <td class="align-middle">
                    <d-input-group>
                      <d-input
                        :min="0"
                        v-model="worker.capability"
                        class="text-right"
                        type="number" />
                      <d-input-group-text slot="append">
                        szt.
                      </d-input-group-text>
                    </d-input-group>
                  </td>
                </tr>
              </tbody>
            </table>

            <div
              v-if="!allDataLoaded"
              class="pb-3 pt-3 text-center">
              Trwa ładowanie danych&hellip;
            </div>

            <d-alert
              v-if="loadError"
              show
              theme="warning">
              Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
            </d-alert>
          </d-tab>
        </d-tabs>
      </d-card>
    </d-form>
  </d-container>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import Vue from 'vue';

export default {
  name: 'procedure-form',
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedWorkers;
    },
    isEdit () {
      return !!this.$route.params.id;
    }
  },
  data() {
    return {
      data: {
        id: 0,
        name: '',
        processes: [],
        users: []
      },
      loaded: false,
      loadError: false,
      loadedWorkers: false,
      workers: []
    };
  },
  watch: {
    allDataLoaded (newValue, oldValue) {
      if (newValue !== oldValue && newValue) {
        let usersCopy = JSON.parse(JSON.stringify(this.data.users));
        let userIDs = usersCopy.map(user => user.id);
        this.data.users = this.workers.map(worker => {
          let workerCapability = '';
          let userIndex = userIDs.indexOf(worker.id);

          if (userIndex > -1) {
            workerCapability = parseInt(usersCopy[userIndex].capability, 10);
          }

          return {
            id: worker.id,
            capability: workerCapability
          };
        });
      }
    }
  },
  mounted () {
    if (this.isEdit) {
      this.loadItemData();
    } else {
      this.loaded = true;
    }

    this.loadAdditionalData();
  },
  methods: {
    loadItemData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/procedures/item/',
        instanceFields: ['id', 'name', 'processes', 'users'],
        loadedKey: 'loaded'
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        outputKey: 'workers',
        loadedKey: 'loadedWorkers',
        errorKey: 'loadError',
        params: {
          pagination: 0,
          where: {
            type: 'WORKER'
          }
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['procedure-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();
      let endpointUrl = this.isEdit ? '/procedures/update/' + this.id : '/procedures/store';

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: 'Zapisano procedurę',
        successText: 'Wybrana procedura została zapisana',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja procedury nie powiodła się. Spróbuj ponownie.',
        successAction: (response) => {
          if (!this.isEdit && response.item && response.item.id) {
            this.$router.push('/procedury/edycja/' + parseInt(response.item.id, 10));
          } else if (this.isEdit) {
            this.loaded = false;
            this.loadItemData();
          } else {
            this.$router.push('/procedury')
          }
        }
      });
    },
    prepareFormDataToSend () {
      let processesToPrepare = JSON.parse(JSON.stringify(this.data.processes));

      processesToPrepare = processesToPrepare.map(process => {
        if (process.type === 'VERIFICATION') {
          process.cost = '0';
        }

        return process;
      });

      let formDataToSend = {
        name: this.data.name,
        processes: processesToPrepare
      };

      if (this.data.users) {
        formDataToSend.users = this.data.users.filter(user => user.capability);
      }

      return formDataToSend;
    },
    addProcess () {
      this.data.processes.push({
        name: '',
        type: '',
        cost: ''
      });
    },
    moveProcess (index, direction) {
      if (direction === 'up') {
        let prev = JSON.parse(JSON.stringify(this.data.processes[index - 1]));
        let toMove = JSON.parse(JSON.stringify(this.data.processes[index]));
        Vue.set(this.data.processes, index - 1, toMove);
        Vue.set(this.data.processes, index, prev);
      } else if (direction === 'down') {
        let next = JSON.parse(JSON.stringify(this.data.processes[index + 1]));
        let toMove = JSON.parse(JSON.stringify(this.data.processes[index]));
        Vue.set(this.data.processes, index + 1, toMove);
        Vue.set(this.data.processes, index, next);
      }
    },
    removeProcess (index) {
      this.data.processes.splice(index, 1);
    },
    getWorkerNameById (id) {
      let foundedWorker = this.workers.filter(worker => worker.id === id);

      if (foundedWorker.length) {
        return foundedWorker[0].name;
      }

      return '???';
    }
  }
};
</script>
